import { Group, Text, Title } from "@mantine/core";
import { Map as OlMap, View } from "ol";
import GeoJSON from "ol/format/GeoJSON";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import XYZ from "ol/source/XYZ";
import { Fill, Stroke, Style } from "ol/style";
import { type ReactNode, useEffect, useRef } from "react";

/**
 * Zaglavlje kartona gazdinstva ili voćnjaka: satelitski isječak sa granicama kao pozadina, naziv i ključni brojevi preko.
 * Granica je ono po čemu se jedinica prepoznaje — zato je ona, a ne ikonica, glavni vizuelni element.
 */
export function UnitHero({ geojson, title, subtitle, badges, actions, stats, onMapClick }: {
  geojson?: unknown; title: ReactNode; subtitle?: ReactNode; badges?: ReactNode; actions?: ReactNode;
  stats?: { label: string; value: ReactNode }[]; onMapClick?: () => void;
}) {
  const el = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!el.current || !geojson) return;
    const gj = (geojson as { type?: string }).type === "FeatureCollection" || (geojson as { type?: string }).type === "Feature"
      ? geojson : { type: "Feature", geometry: geojson, properties: {} };
    const src = new VectorSource({ features: new GeoJSON().readFeatures(gj, { featureProjection: "EPSG:3857" }) });
    // bez geometrije (npr. gazdinstvo bez voćnjaka) zaglavlje ostaje bez karte — ne prikazuje se cijeli svijet
    if (!src.getFeatures().some((f) => f.getGeometry())) return;
    const map = new OlMap({
      target: el.current, controls: [], interactions: [],
      layers: [
        new TileLayer({ source: new XYZ({ url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", maxZoom: 19, crossOrigin: "anonymous" }) }),
        new VectorLayer({ source: src, style: (f) => new Style({
          stroke: new Stroke({ color: f.get("has_3d") ? "#7CFC9A" : "#ffd43b", width: 2.5 }),
          fill: new Fill({ color: "rgba(255,212,59,0.10)" }) }) }),
      ],
      view: new View({ center: [0, 0], zoom: 2 }),
    });
    const ext = src.getExtent();
    if (ext && Number.isFinite(ext[0])) {
      // granica u desnoj polovini (lijevo je tekst)
      const w = el.current.clientWidth;
      map.getView().fit(ext, { padding: [24, 24, 24, Math.round(w * 0.5)], maxZoom: 18 });
    }
    return () => map.setTarget(undefined);
  }, [geojson]);
  return (
    <div className="unit-hero">
      <div ref={el} className="hero-map" onClick={onMapClick} style={{ cursor: onMapClick ? "pointer" : undefined }} />
      <div className="hero-shade" />
      <div className="hero-body">
        {subtitle && <Text size="sm" c="#cfe6c6" mb={2}>{subtitle}</Text>}
        <Title order={2} c="white" lh={1.15}>{title}</Title>
        {badges && <Group gap={6} mt={8}>{badges}</Group>}
        {stats && (
          <Group gap="xl" mt="md">
            {stats.map((s) => (
              <div key={s.label}>
                <Text fw={800} fz={26} lh={1} c="white">{s.value}</Text>
                <Text size="xs" c="#cfe6c6" mt={2}>{s.label}</Text>
              </div>
            ))}
          </Group>
        )}
        {actions && <Group gap="xs" mt="md">{actions}</Group>}
      </div>
    </div>
  );
}
